import React, { useEffect, useRef } from 'react';
import '../assets/scripts/chart.umd.min.js';
import { useUnits } from '../hooks';
import { getElevationColor } from '../utils';
const chartId = 'elevation-chart';
export function ElevationsChart({
  route,
  setChart,
  onHighlight
}) {
  const chartRef = useRef(null);
  const {
    toDistance
  } = useUnits();
  useEffect(() => {
    const {
      Chart,
      CategoryScale,
      Filler,
      LineController,
      LineElement,
      LinearScale,
      PointElement,
      Tooltip,
      Legend
    } = window.Chart;
    Chart.register(LinearScale, CategoryScale, LineController, PointElement, LineElement, Tooltip, Legend, Filler);
    Chart.defaults.font.family = '"Roboto", "Helvetica", "Arial", sans-serif';
  }, []);
  useEffect(() => {
    let _chart;
    if (route && chartRef.current) {
      const Chart = window.Chart.Chart;
      let distance = 0;
      let elevationMin = Number.MAX_VALUE;
      let elevationMax = Number.MIN_VALUE;
      const data = route.sections.reduce((res, section, sectionIndex) => {
        if ('elevations' in section) {
          for (const {
            distanceFromStart: x,
            elevation: y,
            geometryIndex
          } of section.elevations) {
            elevationMin = Math.min(elevationMin, y);
            elevationMax = Math.max(elevationMax, y);
            res.push({
              x: x + distance,
              y,
              sectionIndex,
              geometryIndex
            });
          }
        } else {
          res.push({
            x: Number.NaN,
            y: Number.NaN
          });
        }
        distance += section.distance;
        return res;
      }, []);
      const offset = Math.round(Math.max(50 - (elevationMax - elevationMin), 0) / 2);
      _chart = new Chart(chartRef.current, {
        type: 'line',
        data: {
          datasets: [{
            data,
            borderColor: 'transparent',
            backgroundColor: '#3fb37f18',
            cubicInterpolationMode: 'monotone',
            pointRadius: 0,
            fill: {
              target: 'start'
            }
          }, {
            data,
            borderColor: 'transparent',
            pointBackgroundColor: 'transparent',
            cubicInterpolationMode: 'monotone',
            pointRadius: 1,
            segment: {
              borderColor: ({
                p0,
                p1
              }) => {
                const d = (p1.parsed.x || 0) - (p0.parsed.x || 0);
                const h = (p1.parsed.y || 0) - (p0.parsed.y || 0);
                const slopePercentage = d ? 100 * h / d : 0;
                return getElevationColor(slopePercentage);
              }
            }
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'linear',
              min: 0,
              max: route.distances.total,
              ticks: {
                callback: value => toDistance(value)
              }
            },
            y: {
              type: 'linear',
              min: Math.floor(elevationMin - offset),
              max: Math.ceil(elevationMax + offset),
              offset: true,
              ticks: {
                count: 4,
                callback: value => `${typeof value === 'number' ? Math.round(value) : parseInt(value)} m`
              }
            }
          },
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            tooltip: {
              displayColors: false,
              callbacks: {
                title: ([{
                  parsed: {
                    x
                  }
                }]) => toDistance(x),
                label: ({
                  parsed: {
                    y
                  }
                }) => `${y} m`
              }
            },
            legend: {
              display: false
            }
          },
          onHover: (_, elements) => {
            if (elements[0]) {
              const {
                sectionIndex,
                geometryIndex
              } = data[elements[0].index];
              onHighlight?.(typeof sectionIndex === 'number' && typeof geometryIndex === 'number' ? {
                sectionIndex,
                geometryIndex
              } : null);
            } else {
              onHighlight?.(null);
            }
          }
        }
      });
      setChart?.(_chart);
    }
    return () => {
      if (_chart) _chart.destroy();
    };
  }, [route, chartRef.current]);
  return /*#__PURE__*/React.createElement("canvas", {
    id: chartId,
    onMouseLeave: () => onHighlight?.(null),
    ref: chartRef,
    style: {
      height: 150,
      width: '100%'
    }
  });
}